import { Component, effect, inject, input, signal, untracked } from '@angular/core';
import { ExistingAltAuftraegeRestService } from './service/existing-alt-auftraege-rest.service';
import { ExistingAuftrag } from './model/existing-alt-auftrag.interface';
import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  ButtonDirective,
  GUTACHTEN_AUFTRAGSARTEN_STRINGS,
  ToastService,
  TooltipOnContentOverflowDirective,
} from '@ps-gui/shared-core';
import { Carousel } from 'primeng/carousel';
import { LocalizedDatePipe } from '../../../../pipes/localized-date.pipe';
import { formatMoney, Navigate } from '@ps-gui/shared-utils';
import { Tooltip } from 'primeng/tooltip';
import { HttpParams } from '@angular/common/http';
import { PersistDataService } from '../../../../service/app-state/persist-data.service';
import { CtBatchMetaData, PsNavigationService } from '@ps-gui/claims-terminal-shared';
import { getTenantAltsystemKey } from '../../../../service/transmission/tenant-altsystem.mapper';
import { PrefillService } from '../../../../service/prefill.service';
import { CtBatchRedirectStoreService } from '../../../../service/transmission/ct-batch-redirect-store.service';
import { ExistingGutachtenRestService } from './service/existing-gutachten-rest.service';
import { map, Observable } from 'rxjs';
import { GutachtenSearchResultItem } from './model/gutachten-search-result-item.interface';
import { ExistingAuftraegeSearchParameters } from './model/existing-auftraege-search-parameters.interface';

@Component({
  selector: 'ct-extraction-existing-auftraege-selection',
  imports: [
    Carousel,
    LocalizedDatePipe,
    NgClass,
    TooltipOnContentOverflowDirective,
    Tooltip,
    NgOptimizedImage,
    ButtonDirective,
  ],
  templateUrl: './existing-auftraege-selection.component.html',
  styleUrl: './existing-auftraege-selection.component.scss',
})
export class ExistingAuftraegeSelectionComponent {
  protected readonly formatMoney = formatMoney;

  readonly metaData = input<CtBatchMetaData>();
  readonly searchParameters = input.required<ExistingAuftraegeSearchParameters>();

  private readonly existingAuftraegeRestService = inject(ExistingAltAuftraegeRestService);
  private readonly existingGutachtenRestService = inject(ExistingGutachtenRestService);
  private readonly toastService = inject(ToastService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly redirectStore = inject(CtBatchRedirectStoreService);
  private readonly altfallPrefillService = inject(PrefillService);
  private readonly psNavigationService = inject(PsNavigationService);

  protected readonly existingAuftraege = signal<ExistingAuftrag[]>([]);

  protected readonly selectedAuftrag = signal<ExistingAuftrag | undefined>(undefined);

  protected readonly mandatoryFieldsAreFilled = signal(false);

  constructor() {
    effect(() => {
      const params = this.searchParameters();

      const allFilled = !!params.tenant && !!params.auftragsart && !!params.kopfdaten_schadennummer;

      untracked(() => {
        this.mandatoryFieldsAreFilled.set(allFilled);

        if (allFilled) {
          this.fetchAuftraegeIfMandatoryFieldsAreFilled();
        }
      });
    });
  }

  private fetchAuftraegeIfMandatoryFieldsAreFilled() {
    let obs: Observable<ExistingAuftrag[]>;

    if (this.isGutachtenType()) {
      obs = this.existingGutachtenRestService
        .searchExistingGutachtenAuftraege(this.createGutachtenSearchParams())
        .pipe(
          map((gutachtenAuftraege) =>
            gutachtenAuftraege.results.map((g) => this.mapToExistingAuftrag(g)),
          ),
        );
    } else {
      obs = this.existingAuftraegeRestService.searchExistingAltAuftraege(this.createSearchParams());
    }

    this.toastService
      .runWithToast(obs, 'Suche nach Aufträgen', 'Suche abgeschlossen', 'Suche fehlgeschlagen')
      .subscribe((resp) => this.consumeResponse(resp));
  }

  private isGutachtenType(): boolean {
    const auftragsArt = this.searchParameters().auftragsart;
    return !!auftragsArt && GUTACHTEN_AUFTRAGSARTEN_STRINGS.has(auftragsArt);
  }

  private consumeResponse(resp: ExistingAuftrag[]) {
    for (const auftrag of resp) {
      this.adoptAuftrag(auftrag);
    }
    resp.sort((a, b) => this.sortAuftraege(a, b));
    for (let i = 0; i < resp.length; i++) {
      resp[i].index = i + 1;
    }
    this.existingAuftraege.set(resp);

    if (resp.length === 1 && resp[0].absOrdNrDlp === this.metaData()?.externeId) {
      this.onClickAuftrag(resp[0]);
    }

    const persistedAuftragId = this.persistDataService.altfallZuweisung?.auftragId;

    this.selectedAuftrag.set(undefined);

    if (persistedAuftragId) {
      this.selectedAuftrag.set(this.existingAuftraege().find((a) => a.id === persistedAuftragId));
    }

    // If the user has previously selected an existing Auftrag, but this Auftrag does not exist
    // anymore (i.e. because it was deleted or because the Auftragsart was changed) then we have to clear it.
    if (!this.selectedAuftrag()) {
      this.altfallPrefillService.clear();
    }
  }

  private mapToExistingAuftrag(g: GutachtenSearchResultItem): ExistingAuftrag {
    return {
      id: g.id,
      isLegacySystemAuftrag: false,
      claimNumber: g.schadennummer,
      businessTransaction: g.teilschadennummer,
      claimant: {
        firstName: g.betroffenerVorname,
        lastName: g.betroffenerNachname,
      },
      index: 0,
      schadenNummer: '',
    };
  }

  private createSearchParams(): HttpParams {
    const tenantName = this.searchParameters().tenant;

    if (!tenantName) {
      this.toastService.addErrorToast('Kein Mandant wurde gewählt!');
      throw new Error('Kein Mandant wurde gewählt!');
    }

    // This needs to be decided here because the Auftragtyp can be changed after
    // the tenant was selected.
    const resolvedTenant = this.isGutachtenType()
      ? tenantName
      : getTenantAltsystemKey(tenantName, this.metaData()?.tenants);

    if (!resolvedTenant) {
      this.toastService.addErrorToast('Mandant konnte nicht aufgelöst werden!');
      throw new Error('Mandant konnte nicht aufgelöst werden!');
    }

    let params = new HttpParams();
    params = params.set('tenantName', resolvedTenant);
    params = params.set('claimNumber', this.searchParameters().kopfdaten_schadennummer!);

    const businessTransaction = this.searchParameters().kopfdaten_kennung;
    if (businessTransaction) {
      params = params.set('businessTransaction', businessTransaction);
    }

    return params;
  }

  private createGutachtenSearchParams() {
    let params = new HttpParams();
    params = params.set('mandantUniqueKey', this.searchParameters().tenant!);
    params = params.set('auftragTyp', this.searchParameters().auftragsart!);
    params = params.set('schadennummer', this.searchParameters().kopfdaten_schadennummer!);

    const businessTransaction = this.searchParameters().kopfdaten_kennung;
    if (businessTransaction) {
      params = params.set('teilschadennummer', businessTransaction);
    }

    return params;
  }

  /** Everything else comes from the backend, only the display Schadennummer is derived here. */
  private adoptAuftrag(auftrag: ExistingAuftrag) {
    auftrag.schadenNummer = auftrag.claimNumber;
    auftrag.isLegacySystemAuftrag = !this.isGutachtenType();

    if (auftrag.businessTransaction) {
      auftrag.schadenNummer += '-' + auftrag.businessTransaction;
    }
  }

  protected onClickNeuerAuftrag() {
    this.selectedAuftrag.set(undefined);
    this.altfallPrefillService.clear();
  }

  protected onClickAuftrag(auftrag: ExistingAuftrag) {
    this.selectedAuftrag.set(auftrag);
    this.altfallPrefillService.store(auftrag);
  }

  protected onClickOpenAuftrag(event: MouseEvent, auftrag: ExistingAuftrag) {
    event.stopPropagation();

    if (this.isGutachtenType()) {
      this.psNavigationService.openGutachten(auftrag.id);
    } else {
      Navigate.open(this.redirectStore.buildAuditRedirectUrl(auftrag.id));
    }
  }

  private sortAuftraege(a: ExistingAuftrag, b: ExistingAuftrag): number {
    const byInvoiceDate = this.timestampOf(b.invoiceDate) - this.timestampOf(a.invoiceDate);

    if (byInvoiceDate !== 0) {
      return byInvoiceDate;
    }

    const byBeauftragungsDate =
      this.timestampOf(b.beauftragungsDate) - this.timestampOf(a.beauftragungsDate);

    if (byBeauftragungsDate !== 0) {
      return byBeauftragungsDate;
    }

    return (a.claimAmount ?? 0) - (b.claimAmount ?? 0);
  }

  /** Missing dates sort last instead of turning the whole comparison into NaN. */
  private timestampOf(value?: string): number {
    const time = value ? new Date(value).getTime() : 0;

    return Number.isNaN(time) ? 0 : time;
  }
}
